import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useProfile } from '@/lib/providers';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

interface FAQ {
  id: string;
  question: string;
  answer: string;
}

export default function FAQPage() {
  const { profile } = useProfile();
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [isLoadingFaqs, setIsLoadingFaqs] = useState(true);

  // Fetch FAQs from Supabase
  useEffect(() => {
    const fetchFaqs = async () => {
      setIsLoadingFaqs(true);
      try {
        const roleScope = profile?.role === 'hoofdhartuser' ? 'hoofdhartuser' : null;
        let query = supabase
          .from('faqs')
          .select('*')
          .order('id');

        if (roleScope) {
          query = query.eq('role_scope', roleScope);
        } else {
          query = query.is('role_scope', null);
        }

        const { data, error } = await query;

        if (error) {
          console.error('Error fetching FAQs:', error);
          // Add default FAQs if there's an error fetching from the database
          setFaqs([
            {
              id: '1',
              question: 'Hoe vaak kan ik de app gebruiken?',
              answer:
                'Je kunt de app zo vaak gebruiken als je wilt. Er is geen limiet op het aantal sessies.',
            },
            {
              id: '2',
              question: 'Kan ik mijn abonnement opzeggen?',
              answer:
                'Ja, je kunt je abonnement op elk moment opzeggen via de instellingen in je profiel.',
            },
            {
              id: '3',
              question: 'Hoe werken de meditaties?',
              answer:
                "Onze meditaties zijn ontworpen om je te helpen ontspannen en mindfulness te beoefenen. Je kunt kiezen uit verschillende thema's en lengtes.",
            },
            {
              id: '4',
              question: 'Zijn er offline opties?',
              answer:
                'Momenteel moet je verbonden zijn met internet om de app te gebruiken. We werken aan offline functionaliteit voor toekomstige updates.',
            },
          ]);
          setIsLoadingFaqs(false);
          return;
        }

        if (data && data.length > 0) {
          setFaqs(data);
        } else {
          // Add default FAQs if none are found in the database
          setFaqs([
            {
              id: '1',
              question: 'Hoe vaak kan ik de app gebruiken?',
              answer:
                'Je kunt de app zo vaak gebruiken als je wilt. Er is geen limiet op het aantal sessies.',
            },
            {
              id: '2',
              question: 'Kan ik mijn abonnement opzeggen?',
              answer:
                'Ja, je kunt je abonnement op elk moment opzeggen via de instellingen in je profiel.',
            },
            {
              id: '3',
              question: 'Hoe werken de meditaties?',
              answer:
                "Onze meditaties zijn ontworpen om je te helpen ontspannen en mindfulness te beoefenen. Je kunt kiezen uit verschillende thema's en lengtes.",
            },
            {
              id: '4',
              question: 'Zijn er offline opties?',
              answer:
                'Momenteel moet je verbonden zijn met internet om de app te gebruiken. We werken aan offline functionaliteit voor toekomstige updates.',
            },
          ]);
        }
      } catch (error) {
        console.error('Error fetching FAQs:', error);
        // Add default FAQs if there's an exception
        setFaqs([
          {
            id: '1',
            question: 'Hoe vaak kan ik de app gebruiken?',
            answer:
              'Je kunt de app zo vaak gebruiken als je wilt. Er is geen limiet op het aantal sessies.',
          },
          {
            id: '2',
            question: 'Kan ik mijn abonnement opzeggen?',
            answer:
              'Ja, je kunt je abonnement op elk moment opzeggen via de instellingen in je profiel.',
          },
          {
            id: '3',
            question: 'Hoe werken de meditaties?',
            answer:
              "Onze meditaties zijn ontworpen om je te helpen ontspannen en mindfulness te beoefenen. Je kunt kiezen uit verschillende thema's en lengtes.",
          },
          {
            id: '4',
            question: 'Zijn er offline opties?',
            answer:
              'Momenteel moet je verbonden zijn met internet om de app te gebruiken. We werken aan offline functionaliteit voor toekomstige updates.',
          },
        ]);
      } finally {
        setIsLoadingFaqs(false);
      }
    };

    fetchFaqs();
  }, []);

  return (
    <div className="min-h-screen p-8">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <Button variant="ghost" asChild>
            <Link to="/profiel">
              <ChevronLeft className="h-5 w-5 mr-1" />
              Terug naar profiel
            </Link>
          </Button>
        </div>

        <div className="bg-card rounded-lg p-6 border-2 border-green-500">
          <h3 className="text-lg font-medium mb-4">Veelgestelde vragen</h3>

          {isLoadingFaqs ? (
            <div className="py-4 text-center text-muted-foreground">
              Laden van veelgestelde vragen...
            </div>
          ) : faqs.length > 0 ? (
            <Accordion type="single" collapsible className="w-full mb-4">
              {faqs.map((faq) => (
                <AccordionItem key={faq.id} value={faq.id}>
                  <AccordionTrigger className="text-left font-medium">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          ) : (
            <p className="mb-4 text-muted-foreground">
              Er zijn momenteel geen veelgestelde vragen beschikbaar.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
